import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { LabelProps, PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { findCenter } from '@editor/services/Points'
import { isConvexPolygon } from '@editor/services/Polygon'
import { alignToZAxis } from '@editor/services/Shape'
import { meterToMillimeter } from '@editor/services/Util'
import { useToolUtilSelector } from '@editor/store/selectors'
import { cloneDeep } from 'lodash'
import polylabel from 'polylabel'
import {
  BufferAttribute,
  BufferGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  ShapeUtils,
  Vector2,
  Vector3,
} from 'three'

import { PlanePoints } from '../definitions'
import useConfigs from './useConfigs'

/**
 * Minimum allowed squared distance between two points.
 * Used to determine if two points are considered "distinct" in geometric operations.
 * Units: squared scene units (e.g., if positions are in meters, this is in m^2).
 * The value 0.001 was chosen to avoid floating-point precision issues and to ignore negligible differences.
 */
const MIN_DISTANCE_SQUARED = 0.001

const useObjects = (
  props: PolygonPlaneMeshProps,
  currentPositionsRef: MutableRefObject<PointArray[] | null>,
  dragStartPosRef: MutableRefObject<PointArray | null>,
  isDraggingRef: MutableRefObject<boolean>,
) => {
  const {
    id,

    polygon,
    offsetTransformation,
    transformControls,

    isOpenLoop,
    isEditing,
    isWorking,
    isAdjustingColors = false,

    visible = true,
    opacity = 1,

    disableEditingRef,

    onUpdate,
    getPoints,
  } = props
  const { guideLabelConfig, labelConfig } = useConfigs(props)

  // Store
  const currentAnchorPosition = useToolUtilSelector((state) => state.currentAnchorPosition)
  const currentFillColor = useToolUtilSelector((state) => state.currentFillColor)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)

  // States
  const [labelText, setLabelText] = useState<string>('')

  // Refs
  const editingAnchorRef = useRef<number | null>(null)
  const meshRef = useRef<Mesh | null>(null)

  /**
   * Setup offset matrix.
   * Use whatever is provided, or identity if none.
   */
  const offsetMatrix = useMemo(() => {
    if (offsetTransformation instanceof Matrix4) return offsetTransformation
    return offsetTransformation ? new Matrix4().fromArray(offsetTransformation) : new Matrix4().identity()
  }, [offsetTransformation])
  const polygonMatrixInverse = useMemo(
    () =>
      polygon.transformation && transformControls
        ? new Matrix4().fromArray(polygon.transformation).invert()
        : new Matrix4().identity(),
    [polygon.transformation, transformControls],
  )

  /**
   * Get position from offset matrix.
   */
  const { position: offsetPosition, quaternion: offsetQuaternion } = useMemo(() => {
    if (!offsetTransformation) return { position: new Vector3(), quaternion: new Quaternion(), matrix: new Matrix4() }

    const pos = new Vector3()
    const quat = new Quaternion()

    offsetMatrix.decompose(pos, quat, new Vector3())

    return {
      position: pos,
      quaternion: quat,
      matrix: offsetMatrix,
    }
  }, [offsetTransformation, offsetMatrix])

  // Vars
  const isVisible = useMemo(() => visible && opacity > 0, [visible, opacity])
  const planeCenter = useMemo(() => new Vector3(...polygon.center!), [polygon.center])

  /**
   * Use states for points for ID stability.
   */
  const [planePoints, setPlanePoints] = useState<PlanePoints[]>(() =>
    polygon.positions.map((p) => ({
      id: crypto.randomUUID(),
      point: new Vector3(...p),
    })),
  )
  const planePointsArr = useMemo(() => planePoints.map((p) => p.point.toArray()), [planePoints])

  /**
   * Calculate label position at the centroid of the polygon.
   * We're using polylabel to ensure the label is within the polygon
   * if the shape is concave.
   */
  const calculateLabelPosition = useCallback(
    (positions: PointArray[]) => {
      const posV3 = positions.map((v) => new Vector3(...v))

      // If the polygon is convex, we can use simple center calculation
      const { points: zAlignedPosV3 } = alignToZAxis(posV3)
      if (isConvexPolygon(zAlignedPosV3)) {
        const center = findCenter(posV3)
        return center || planeCenter
      }

      const { points, inverseMatrix, offset } = alignToZAxis(posV3, true)

      const centered = polylabel([points.map((pos) => [pos.x, pos.y])], 0.001)
      return new Vector3(centered[0], centered[1], points[0].z).applyMatrix4(inverseMatrix).add(offset)
    },
    [planeCenter],
  )

  const [labelPosition, setLabelPosition] = useState(() => calculateLabelPosition(polygon.positions))

  // Update label position when polygon positions change
  useEffect(() => {
    setLabelPosition(calculateLabelPosition(polygon.positions))
  }, [polygon.positions, calculateLabelPosition])

  /**
   * Generate plane vertices.
   */
  const planeVertices = useMemo(() => {
    // Non-working polygon use provided vertices as-is.
    if (!isWorking) return polygon.vertices.map((v) => new Vector2(...v))

    // If working, we need to add the current anchor and recalculate the vertices.
    // Generate vertices once we actually have a triangle.
    const { points: verticesV3 } = alignToZAxis(planePoints.map((p) => p.point))
    return verticesV3.map((v) => new Vector2(...v))
  }, [polygon.vertices, planePoints, isWorking])

  /**
   * Generate plane points.
   */
  const getPlanePoints = useCallback(
    (currentPos: PointArray, anchorIndex: number | null) => {
      const pts = cloneDeep(polygon.positions)

      // new polygon
      if (isWorking && currentPos) {
        if (getPoints) {
          return getPoints(currentPos, null)
        }

        // Need to do a bit of clean up for points that are too close to each other
        const lastPoint = pts[pts.length - 1]
        if (lastPoint) {
          const newPoint = currentPos
          const distanceSquared =
            (lastPoint[0] - newPoint[0]) ** 2 + (lastPoint[1] - newPoint[1]) ** 2 + (lastPoint[2] - newPoint[2]) ** 2

          // Points are too close; ignore the new point
          if (distanceSquared < MIN_DISTANCE_SQUARED) {
            return pts
          }
        }

        pts.push(currentPos)
      }

      // adjusting a single vertex
      if (anchorIndex !== null && currentPos) {
        pts[anchorIndex] = currentPos
      }

      // dragging the entire polygon
      if (isEditing && anchorIndex === null) {
        if (getPoints) {
          return getPoints(currentPos, anchorIndex)
        }

        if (!dragStartPosRef.current) {
          dragStartPosRef.current = currentPos
        } else {
          const dragOffset = [
            currentPos[0] - dragStartPosRef.current[0],
            currentPos[1] - dragStartPosRef.current[1],
            currentPos[2] - dragStartPosRef.current[2],
          ]
          pts.forEach((p) => {
            p[0] += dragOffset[0]
            p[1] += dragOffset[1]
            p[2] += dragOffset[2]
          })
        }
      }

      return pts
    },
    [polygon.positions, isWorking, isEditing, dragStartPosRef, getPoints],
  )

  /**
   * Generate guide line labels.
   */
  const guideLabels = useMemo((): LabelProps[] => {
    if (!guideLabelConfig || planePoints.length < 2) return []

    // Calculate the positions and label for the labels on midpoints of the edges.
    const pts = planePoints.map((p) => p.point.toArray())
    const points = isOpenLoop ? pts : pts.slice(0, -1)
    let positions = points.map((point, index) => {
      const nextPoint = new Vector3(...points[(index + 1) % points.length])
      return {
        point: new Vector3(...point).clone().add(nextPoint).multiplyScalar(0.5).toArray(),
        label: `${Math.round(meterToMillimeter(new Vector3(...point).distanceTo(nextPoint)))}mm`,
      }
    })

    // Hide the last label if hideLastLabel is true
    if (guideLabelConfig.hideLastLabel && positions.length > 0) {
      positions = positions.slice(0, -1)
    }

    return positions.map(({ point, label: lbl }, index) => ({
      ...guideLabelConfig,
      id: `${polygon.shape_id}-label-${index}`,
      point,
      label: lbl,
      labelPrefix: '',
    }))
  }, [planePoints, guideLabelConfig, isOpenLoop, polygon.shape_id])

  /**
   * Create geometry in a state for optimization.
   * It'll be created once, and updated when the points change.
   */
  const [geometry] = useState<BufferGeometry>(() => {
    const buffer = new BufferGeometry()
    const positions = new Float32Array(planePoints.length * 3)
    planePoints.forEach(({ point: vertex }, index) => {
      vertex.toArray(positions, index * 3)
    })

    const indi = ShapeUtils.triangulateShape(planeVertices, [])

    buffer.setAttribute('position', new BufferAttribute(positions, 3))
    buffer.setIndex(indi.flat())
    buffer.computeVertexNormals()

    return buffer
  })

  /**
   * Update mesh function
   */
  const updateMesh = useCallback(
    (points: PointArray[]) => {
      // Set new positions
      const positions = new Float32Array(points.length * 3)
      points.forEach((point, index) => {
        positions.set(point, index * 3)
      })
      geometry.setAttribute('position', new BufferAttribute(positions, 3))

      // Generate z-aligned vertices since triangulation depends on the points to be on a plane.
      const alignedPoints = alignToZAxis(points.map((v) => new Vector3(...v)))
      const indi = ShapeUtils.triangulateShape(alignedPoints.points, [])
      geometry.setIndex(indi.flat())

      geometry.computeBoundingBox()
      geometry.computeBoundingSphere()

      meshRef.current?.updateMatrixWorld()
    },
    [geometry],
  )

  /**
   * Update the geometry when the plane points change.
   */
  useEffect(() => {
    if (
      !planeCenter ||
      !currentAnchorPosition ||
      !isVisible ||
      (!isEditing && !isWorking) ||
      (isEditing && editingAnchorRef.current === null && isDraggingRef.current === false) ||
      disableEditingRef?.current
    )
      return

    const point = new Vector3(...currentAnchorPosition)

    // If offsetTransformation is set, apply the transformation to currentPoint
    if (polygonMatrixInverse) {
      point.applyMatrix4(polygonMatrixInverse)
    }

    const points = getPlanePoints(point.toArray(), editingAnchorRef.current)
    currentPositionsRef.current = points

    // -- Update mesh
    updateMesh(points)
    if (labelConfig) setLabelPosition(calculateLabelPosition(points))
    if (labelConfig?.generateLabel) setLabelText(labelConfig.generateLabel(points))

    // -- Update line / anchor
    setPlanePoints((prev) =>
      points.map((p, i) =>
        prev[i] ? { ...prev[i], point: new Vector3(...p) } : { id: crypto.randomUUID(), point: new Vector3(...p) },
      ),
    )

    // Callbacks
    onUpdate?.(points)
  }, [
    meshRef,
    disableEditingRef,
    planeCenter,
    currentAnchorPosition,
    geometry,
    isWorking,
    isEditing,
    isVisible,
    isDraggingRef,
    editingAnchorRef,
    currentPositionsRef,
    labelConfig,
    polygonMatrixInverse,
    calculateLabelPosition,
    updateMesh,
    getPlanePoints,
    onUpdate,
  ])

  /**
   * Update plane points when polygon positions change.
   */
  useEffect(() => {
    updateMesh(polygon.positions)
    setPlanePoints((prev) =>
      polygon.positions.map((p, i) =>
        prev[i] ? { ...prev[i], point: new Vector3(...p) } : { id: crypto.randomUUID(), point: new Vector3(...p) },
      ),
    )
  }, [id, polygon.positions, updateMesh])

  /**
   * Update the mesh material color when the current fill color changes.
   * This is used for real-time updates of the fill color while the user is selecting it to
   * avoid unnecessary re-renders.
   *
   * The color set here _should not_ be the final color. It still must come from the `color` prop.
   */
  useEffect(() => {
    if (
      !meshRef.current ||
      !(meshRef.current.material instanceof MeshBasicMaterial) ||
      !currentFillColor ||
      !isAdjustingColors
    )
      return

    meshRef.current.visible = currentFillColor !== 'transparent'
    meshRef.current.material.color.set(currentFillColor)
  }, [currentFillColor, selectedElementIds, id, isWorking, isAdjustingColors])

  /**
   * Clean up geometry on unmount
   */
  useEffect(() => () => geometry && geometry.dispose(), [geometry])

  return {
    offsetPosition,
    offsetQuaternion,

    planePoints,
    planePointsArr,

    geometry,
    guideLabels,

    labelPosition,
    labelText,

    meshRef,
    editingAnchorRef,

    isVisible,
  }
}

export default useObjects
